import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useObSurveysApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function ObSurveysPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);

  // drawer: { mode: 'create' } | { mode: 'edit', initialValues }
  const [drawer, setDrawer] = useState(null);

  const {
    fetching, data, pagination, columns: baseColumns,
    objectSchema, toFormValues, create, update, remove,
  } = useObSurveysApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleNewButtonClick = useCallback(() => setDrawer({ mode: 'create' }), []);

  const handleSave = useCallback(async (formData) => {
    const success = drawer?.mode === 'edit'
      ? await update(drawer.initialValues.id, formData)
      : await create(formData);
    if (success) setDrawer(null);
    return success;
  }, [drawer, create, update]);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    return [
      {
        type: 'edit',
        label: t('common.edit'),
        onClick: () => setDrawer({ mode: 'edit', initialValues: toFormValues(row) }),
      },
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, id: row.id, name: row.name }),
      },
    ];
  }, [t, writable, toFormValues]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.mode === 'edit' ? t('onboardingSurvey.edit') : t('onboardingSurvey.new')}
        schema={objectSchema}
        initialValues={drawer?.mode === 'edit' ? drawer.initialValues : null}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('onboardingSurvey.deleteConfirm', { name: deleteDialog.name })}
        onConfirm={async () => { setBusy(true); await remove(deleteDialog.id); setDeleteDialog({ isOpen: false, id: null, name: '' }); setBusy(false); }}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      {useMemo(() => (
        <DataTable
          title={t('onboardingSurvey.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
